import { FixingMode } from "@/data/fixingMode";
import { z } from "zod";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";

import { Form } from "@/components/ui/form";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
} from "@heroui/react";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

import { Diff } from "@/features/FormCard/Diff";

import { DatePickerField, InputField } from "@/features/FormCard/Fields";

import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { dateSchema, parseToDateValue } from "@/features/FormCard/utils";

import { FormActionsUpdate } from "@/features/FormCard/Actions";

import { ExternalLink } from "@/components/ExternalLink";
import { useEditForm } from "@/features/Edit/hooks";
import { fieldTranslations } from "./translations";
import {
  computeFixingMode,
  requiresFixing,
  requiresMarketValue,
} from "./utils";
import { useMemo } from "react";

const FormSchema = z
  .object({
    date: z.preprocess((val) => parseToDateValue(val as string), dateSchema),
    n1_static_datum_id: z.number().nullable().optional(),
    n1_static_datum_name: z.string().nullable().optional(),
    fixing: z.coerce.number().nullable().optional(),
    market_value: z.coerce.number().nullable().optional(),
    currency: z.string().nullable().optional(),
    n1_static_datum_asset_class_2: z.string().nullable().optional(),
    // needed for technical info card (tihs component use the query cache)
    created_at: z.string().nullable().optional(),
    updated_at: z.string().nullable().optional(),
    time_serie_file_id: z.number().nullable().optional(),
    n1_time_serie_file_id: z.number().nullable().optional(),
  })
  .refine(requiresMarketValue, {
    message: i18n.t("time_series.errors.market_value_required"),
    path: ["market_value"],
  })
  .refine(requiresFixing, {
    message: i18n.t("time_series.errors.unit_price_required"),
    path: ["fixing"],
  });

export const formFields = FormSchema.keyof().enum;

export function N1TimeSeriesEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({
    id,
    client,
    table_name,
    FormSchema,
  });

  const n1_static_datum_id = form.watch(formFields.n1_static_datum_id);

  const asset_class_2 = form.watch(formFields.n1_static_datum_asset_class_2);

  const fixingMode: FixingMode | null = useMemo(
    () => computeFixingMode(asset_class_2),
    [asset_class_2],
  );

  return (
    <div className="grid place-items-center bg-gray-50 h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t("time_series.price_valuation", {
                  defaultValue: "Price / valuation",
                })}{" "}
                {id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                <InputField
                  name={formFields.n1_static_datum_name}
                  label={t(`${table_name}.columns.n1_static_datum_name`, {
                    ns: "tables",
                    defaultValue: "Asset",
                  })}
                  control={form.control}
                  fieldOptions={{
                    isDisabled: true,
                  }}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name={formFields.date}
                    label={t(`${table_name}.columns.date`, {
                      ns: "tables",
                      defaultValue: "Date",
                    })}
                    isRequired={true}
                    control={form.control}
                  />
                  <InputField
                    name={formFields.currency}
                    label={t(`${table_name}.columns.currency`, {
                      ns: "tables",
                      defaultValue: "Currency",
                    })}
                    control={form.control}
                    fieldOptions={{
                      isDisabled: true,
                    }}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {fixingMode === FixingMode.VALORISATION && (
                    <InputField
                      name={formFields.market_value}
                      label={t(`${table_name}.columns.market_value`, {
                        ns: "tables",
                        defaultValue: "Market value",
                      })}
                      isRequired
                      control={form.control}
                      type="number"
                    />
                  )}
                  {fixingMode === FixingMode.FIXING && (
                    <InputField
                      name={formFields.fixing}
                      label={t(`${table_name}.columns.fixing`, {
                        ns: "tables",
                        defaultValue: "Unit price",
                      })}
                      control={form.control}
                      isRequired={true}
                      type="number"
                    />
                  )}
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={true}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>
        <div className="absolute top-14 right-14 2xl:top-10 2xl:right-10">
          {n1_static_datum_id && (
            <Button
              href={toWorkspacePath(
                `/admin/${Clients.N1}/${Tables.N1_STATIC_DATA}/${n1_static_datum_id}`,
              )}
              variant="bordered"
              color="primary"
              showAnchorIcon
              as={ExternalLink}
              radius="full"
              className="bg-white border-1 border-neutral-300 shadow-xl"
            >
              {t("transactions.asset", { defaultValue: "Asset" })}
            </Button>
          )}
        </div>
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_update")}
          children={
            changedFields
              ? Diff(
                  changedFields,
                  ["n1_static_datum_id"],
                  fieldTranslations,
                  table_name,
                )
              : t("no_changes")
          }
        />

        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onDelete={handleDelete}
          isLoading={isDeleting}
          requireTyping
        />
      </div>
    </div>
  );
}
